import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const NewPolicyModal = ({ isOpen, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    department: '',
    framework: '',
    description: '',
    version: '1.0',
    effectiveDate: '',
    expirationDate: '',
    priority: 'Medium',
    owner: '',
    reviewCycle: '12', // months
    tags: [],
    approvers: [],
    attachments: []
  });

  const [currentTag, setCurrentTag] = useState('');
  const [currentApprover, setCurrentApprover] = useState('');

  const categories = [
    'Security', 'Privacy', 'Financial', 'HR', 'IT', 'Operations', 'Legal', 'Quality', 'Environmental'
  ];

  const departments = [
    'Information Technology', 'Human Resources', 'Finance', 'Operations', 
    'Legal', 'Marketing', 'Sales', 'Procurement', 'Quality Assurance'
  ];

  const frameworks = [
    'SOX', 'GDPR', 'ISO 27001', 'NIST', 'COSO', 'ITIL', 'HIPAA', 'PCI DSS', 'COBIT'
  ];

  const priorities = ['Low', 'Medium', 'High', 'Critical'];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleAddTag = () => {
    if (currentTag?.trim() && !formData?.tags?.includes(currentTag?.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev?.tags, currentTag?.trim()]
      }));
      setCurrentTag('');
    }
  };

  const handleRemoveTag = (tag) => {
    setFormData(prev => ({
      ...prev,
      tags: prev?.tags?.filter(t => t !== tag)
    }));
  };

  const handleAddApprover = () => {
    if (currentApprover?.trim() && !formData?.approvers?.includes(currentApprover?.trim())) {
      setFormData(prev => ({
        ...prev,
        approvers: [...prev?.approvers, currentApprover?.trim()]
      }));
      setCurrentApprover('');
    }
  };

  const handleRemoveApprover = (approver) => {
    setFormData(prev => ({
      ...prev,
      approvers: prev?.approvers?.filter(a => a !== approver)
    }));
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    
    // Validate required fields
    if (!formData?.name || !formData?.category || !formData?.department || !formData?.description) {
      alert('Please fill in all required fields');
      return;
    }

    // Create new policy object
    const newPolicy = {
      ...formData,
      id: `POL-${Math.random()?.toString(36)?.substr(2, 3)?.toUpperCase()}`,
      status: 'Draft',
      lastUpdated: new Date()?.toISOString()?.split('T')?.[0],
      approvedBy: 'Pending',
      documentSize: `${Math.floor(Math.random() * 3 + 1)}.${Math.floor(Math.random() * 9)}MB`,
      downloadCount: 0,
      relatedPolicies: [],
      approvalChain: formData?.approvers?.map(approver => ({
        name: approver,
        role: 'Approver',
        date: '',
        status: 'Pending'
      })),
      versionHistory: [{
        version: formData?.version,
        date: new Date()?.toISOString()?.split('T')?.[0],
        changes: 'Initial policy creation'
      }]
    };

    onSave(newPolicy);
    onClose();
    
    // Reset form
    setFormData({
      name: '',
      category: '',
      department: '',
      framework: '',
      description: '',
      version: '1.0',
      effectiveDate: '',
      expirationDate: '',
      priority: 'Medium',
      owner: '',
      reviewCycle: '12',
      tags: [],
      approvers: [],
      attachments: []
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div className="bg-dark-elevated rounded-neumorphic shadow-neumorphic w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 border-b border-dark-surface">
          <h2 className="text-xl font-semibold text-text-dark-primary">Create New Policy</h2>
          <button
            onClick={onClose}
            className="neumorphic-button p-2 text-text-dark-secondary hover:text-text-dark-primary"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Modal Content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-text-dark-primary mb-2">
                Policy Name *
              </label>
              <input
                type="text"
                value={formData?.name}
                onChange={(e) => handleInputChange('name', e?.target?.value)}
                className="neumorphic-input w-full px-3 py-2"
                placeholder="Enter policy name"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-text-dark-primary mb-2">
                Category *
              </label>
              <select
                value={formData?.category}
                onChange={(e) => handleInputChange('category', e?.target?.value)}
                className="neumorphic-input w-full px-3 py-2"
                required
              >
                <option value="">Select category</option>
                {categories?.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-text-dark-primary mb-2">
                Department *
              </label>
              <select
                value={formData?.department}
                onChange={(e) => handleInputChange('department', e?.target?.value)}
                className="neumorphic-input w-full px-3 py-2"
                required
              >
                <option value="">Select department</option>
                {departments?.map(dept => (
                  <option key={dept} value={dept}>{dept}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-text-dark-primary mb-2">
                Framework
              </label>
              <select
                value={formData?.framework}
                onChange={(e) => handleInputChange('framework', e?.target?.value)}
                className="neumorphic-input w-full px-3 py-2"
              >
                <option value="">Select framework</option>
                {frameworks?.map(framework => (
                  <option key={framework} value={framework}>{framework}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-text-dark-primary mb-2">
                Version
              </label>
              <input
                type="text"
                value={formData?.version}
                onChange={(e) => handleInputChange('version', e?.target?.value)}
                className="neumorphic-input w-full px-3 py-2"
                placeholder="1.0"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-text-dark-primary mb-2">
                Priority
              </label>
              <select
                value={formData?.priority}
                onChange={(e) => handleInputChange('priority', e?.target?.value)}
                className="neumorphic-input w-full px-3 py-2"
              >
                {priorities?.map(priority => (
                  <option key={priority} value={priority}>{priority}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Dates */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-text-dark-primary mb-2">
                Effective Date
              </label>
              <input
                type="date"
                value={formData?.effectiveDate}
                onChange={(e) => handleInputChange('effectiveDate', e?.target?.value)}
                className="neumorphic-input w-full px-3 py-2"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-text-dark-primary mb-2">
                Expiration Date
              </label>
              <input
                type="date"
                value={formData?.expirationDate}
                onChange={(e) => handleInputChange('expirationDate', e?.target?.value)}
                className="neumorphic-input w-full px-3 py-2"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-text-dark-primary mb-2">
                Review Cycle (months)
              </label>
              <input
                type="number"
                value={formData?.reviewCycle}
                onChange={(e) => handleInputChange('reviewCycle', e?.target?.value)}
                className="neumorphic-input w-full px-3 py-2"
                min="1"
                max="60"
              />
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-text-dark-primary mb-2">
              Description *
            </label>
            <textarea
              value={formData?.description}
              onChange={(e) => handleInputChange('description', e?.target?.value)}
              className="neumorphic-input w-full px-3 py-2 h-32 resize-none"
              placeholder="Enter policy description and objectives"
              required
            />
          </div>

          {/* Policy Owner */}
          <div>
            <label className="block text-sm font-medium text-text-dark-primary mb-2">
              Policy Owner
            </label>
            <input
              type="text"
              value={formData?.owner}
              onChange={(e) => handleInputChange('owner', e?.target?.value)}
              className="neumorphic-input w-full px-3 py-2"
              placeholder="Enter policy owner name"
            />
          </div>

          {/* Tags */}
          <div>
            <label className="block text-sm font-medium text-text-dark-primary mb-2">
              Tags
            </label>
            <div className="flex items-center space-x-2 mb-2">
              <input
                type="text"
                value={currentTag}
                onChange={(e) => setCurrentTag(e?.target?.value)}
                onKeyPress={(e) => e?.key === 'Enter' && (e?.preventDefault(), handleAddTag())}
                className="neumorphic-input flex-1 px-3 py-2"
                placeholder="Add a tag"
              />
              <button
                type="button"
                onClick={handleAddTag}
                className="btn-secondary-dark px-3 py-2"
              >
                Add
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {formData?.tags?.map(tag => (
                <span key={tag} className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-neon-purple bg-opacity-20 text-neon-purple">
                  {tag}
                  <button
                    type="button"
                    onClick={() => handleRemoveTag(tag)}
                    className="ml-1 text-neon-purple hover:text-white"
                  >
                    <Icon name="X" size={12} />
                  </button>
                </span>
              ))}
            </div>
          </div>

          {/* Approvers */}
          <div>
            <label className="block text-sm font-medium text-text-dark-primary mb-2">
              Approvers
            </label>
            <div className="flex items-center space-x-2 mb-2">
              <input
                type="text"
                value={currentApprover}
                onChange={(e) => setCurrentApprover(e?.target?.value)}
                onKeyPress={(e) => e?.key === 'Enter' && (e?.preventDefault(), handleAddApprover())}
                className="neumorphic-input flex-1 px-3 py-2"
                placeholder="Add approver name"
              />
              <button
                type="button"
                onClick={handleAddApprover}
                className="btn-secondary-dark px-3 py-2"
              >
                Add
              </button>
            </div>
            <div className="space-y-2">
              {formData?.approvers?.map(approver => (
                <div key={approver} className="flex items-center justify-between neumorphic-card p-3">
                  <span className="text-text-dark-primary">{approver}</span>
                  <button
                    type="button"
                    onClick={() => handleRemoveApprover(approver)}
                    className="text-error-dark hover:text-error-light"
                  >
                    <Icon name="Trash2" size={16} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Modal Actions */}
          <div className="flex items-center justify-end space-x-3 pt-6 border-t border-dark-surface">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary-dark px-4 py-2"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary-dark px-4 py-2 inline-flex items-center space-x-2"
            >
              <Icon name="Plus" size={16} />
              <span>Create Policy</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NewPolicyModal;